import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Code,
  Group,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { PageShell } from "@/components/PageShell";
import { Section } from "@/components/Section";
import { KpiCard } from "@/components/KpiCard";
import { LoadingState, ErrorState } from "@/components/LoadingState";
import { OpenNowBadge } from "@/components/OpenNowBadge";
import { OperatingHoursTable } from "@/components/OperatingHoursTable";
import {
  useStore,
  useAtlasStoreByLebronId,
  type LebronStore,
} from "@/lib/queries";
import { fmt, durationMinutes, openMinutes, localTimeIn } from "@/lib/format";

export function StoreDetailPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const query = useStore(storeId);

  return (
    <PageShell
      title={query.data?.name ?? "Loading…"}
      subtitle={
        query.data ? (
          <>
            <Text component="span" ff="monospace">
              {query.data.site_id}
            </Text>{" "}
            · {query.data.slug} ·{" "}
            <Link to="/" style={{ color: "inherit" }}>
              ← All stores
            </Link>
          </>
        ) : null
      }
      actions={
        query.data ? (
          <Group gap="xs">
            {query.data.needs_coverage_qa ? (
              <Badge color="yellow" variant="light">
                Coverage QA
              </Badge>
            ) : null}
            <OpenNowBadge
              timezone={query.data.timezone}
              openingTime={query.data.opening_time}
              closingTime={query.data.closing_time}
            />
          </Group>
        ) : null
      }
    >
      {query.isLoading ? <LoadingState label="Loading store…" /> : null}
      {query.error ? <ErrorState error={query.error} /> : null}
      {query.data ? <StoreContent store={query.data} /> : null}
    </PageShell>
  );
}

function StoreContent({ store }: { store: LebronStore }) {
  return (
    <Stack gap="lg">
      <BatchSchedule store={store} />
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        <Identity store={store} />
        <Hours store={store} />
      </SimpleGrid>
      <RawRecord store={store} />
    </Stack>
  );
}

// ───────── OOS batch cadence ────────────────────────────────────────────────

function BatchSchedule({ store }: { store: LebronStore }) {
  const configured =
    store.oos_event_batch_interval != null ||
    store.oos_event_batch_maximum != null;

  return (
    <Section
      title="OOS batch schedule"
      subtitle="How often out-of-stock events are gathered into a batch for this store."
    >
      {configured ? (
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          <KpiCard
            label="Batch interval"
            value={fmt.duration(store.oos_event_batch_interval)}
          />
          <KpiCard
            label="Max events per batch"
            value={
              store.oos_event_batch_maximum != null
                ? store.oos_event_batch_maximum
                : "—"
            }
          />
          <KpiCard
            label="Batches per open day"
            value={batchesPerDay(
              store.oos_event_batch_interval,
              store.opening_time,
              store.closing_time,
            )}
            sub="Derived from the interval and the store's open window"
          />
        </SimpleGrid>
      ) : (
        <Text c="dimmed" size="sm">
          No batch cadence configured — this store uses the service default.
        </Text>
      )}
    </Section>
  );
}

// ───────── Identity ─────────────────────────────────────────────────────────

function Identity({ store }: { store: LebronStore }) {
  // Atlas is the only source of `address`, joined on its `lebron_id`.
  const atlas = useAtlasStoreByLebronId(store.id);

  return (
    <Section title="Identity">
      <Table variant="vertical" withTableBorder={false}>
        <Table.Tbody>
          <Row label="Lebron ID" value={store.id} mono />
          <Row label="Site ID" value={store.site_id} mono />
          <Row label="Slug" value={store.slug} mono />
          <Row
            label="Atlas ID"
            value={store.atlas_id ?? "—"}
            mono={Boolean(store.atlas_id)}
          />
          <Row label="Retailer ID" value={store.retailer_id ?? "—"} />
          <Row
            label="Address"
            value={atlas.data?.address ?? (atlas.isLoading ? "…" : "—")}
          />
          <Row label="Locale" value={store.locale} />
          <Row label="Created" value={fmt.dateTime(store.created_at)} />
          <Row label="Updated" value={fmt.dateTime(store.updated_at)} />
        </Table.Tbody>
      </Table>
    </Section>
  );
}

// ───────── Hours ────────────────────────────────────────────────────────────

function Hours({ store }: { store: LebronStore }) {
  return (
    <Section
      title="Time & hours"
      subtitle="All times are the store's own wall clock, not yours."
    >
      <Stack gap="md">
        <Table variant="vertical" withTableBorder={false}>
          <Table.Tbody>
            <Row label="Timezone" value={store.timezone} />
            <Row
              label="Local time now"
              value={localTimeIn(store.timezone) ?? "—"}
              mono
            />
            <Row
              label="Default window"
              value={fmt.hourRange(store.opening_time, store.closing_time)}
              mono
            />
            <Row
              label="Hours per day"
              value={openSpan(store.opening_time, store.closing_time)}
            />
          </Table.Tbody>
        </Table>

        <div>
          <Text size="sm" fw={600} mb="xs">
            Per-day schedule
          </Text>
          <OperatingHoursTable store={store} />
        </div>
      </Stack>
    </Section>
  );
}

// ───────── Raw record ───────────────────────────────────────────────────────

function RawRecord({ store }: { store: LebronStore }) {
  return (
    <Section
      title="Raw record"
      subtitle={<>Exactly what GET /v1/stores/{"{store_id}"} returned.</>}
    >
      <Code block>{JSON.stringify(store, null, 2)}</Code>
    </Section>
  );
}

function Row({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | number;
  mono?: boolean;
}) {
  return (
    <Table.Tr>
      <Table.Th w={150}>
        <Text size="sm" c="dimmed" fw={400}>
          {label}
        </Text>
      </Table.Th>
      <Table.Td>
        <Text size="sm" ff={mono ? "monospace" : undefined}>
          {value}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
}

function openSpan(
  opening: string | null | undefined,
  closing: string | null | undefined,
): string {
  const minutes = openMinutes(opening, closing);
  if (minutes === null) return "—";
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

/**
 * How many batch windows fit inside a store's open hours — the practical read
 * of the cadence, which a bare interval does not give an operator.
 */
function batchesPerDay(
  interval: string | null | undefined,
  opening: string | null | undefined,
  closing: string | null | undefined,
): string {
  const intervalMinutes = durationMinutes(interval);
  const open = openMinutes(opening, closing);
  if (intervalMinutes === null || open === null || open === 0) return "—";
  return `≈ ${Math.floor(open / intervalMinutes)}`;
}
